/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import {
  Box, Stack, Skeleton, Dialog, DialogTitle, DialogContent, Button, Alert, AlertTitle, Typography, TextField, Autocomplete,
} from '@mui/material';
import { TripRequest } from '@marageti/z4-sdk/lib/travel';
import { LoadingButton } from '@mui/lab';
import {
  ComboBoxOption, getFormProps, getSubmitButtonProps, RichTextEditor,
} from '@marageti/z4-lib';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';
import { z } from 'zod';

type NotesFormDefaultValues = {
  specialNotes: { label: string, value: string }[],
  additionalComments: string | undefined,
};

const DEFAULT_VALUES = {
  specialNotes: [],
  additionalComments: undefined,
} as NotesFormDefaultValues;

const schema = z.object({
  specialNotes: z.array(z.any()).optional(),
  additionalComments: z.string().optional(),
});

type EditNotesViewProps = {
  tripRequest?: TripRequest | undefined;
  open: boolean;
  onClose: (event: {}, reason?: string) => void;
  specialNotesOptions: any[],
  defaultValues?: NotesFormDefaultValues,
  onSubmit: (data: any) => void,
  submissionError: ApiError | undefined,
  onCancel?: () => void
};

const EditNotesModalView = ({
  tripRequest,
  open,
  onClose,
  specialNotesOptions,
  defaultValues,
  onSubmit,
  submissionError,
  onCancel = undefined,
}: EditNotesViewProps) => {
  const {
    control,
    handleSubmit,
    formState: { isSubmitting, errors, isValid },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: defaultValues || DEFAULT_VALUES,
    mode: 'onChange',
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pt: 4, px: 3 }}>
        Edit Partner Notes
      </DialogTitle>

      <DialogContent sx={{ padding: 0 }}>
        <Box component="form" {...getFormProps(handleSubmit(onSubmit))}>
          <Box sx={{
            paddingTop: 0, overflowY: 'auto', px: 3, maxHeight: '60vh',
          }}
          >
            <Box sx={{ pb: 4 }}>
              {!tripRequest ? (
                <Stack spacing={2} mt="20px">
                  {Array(2).fill(null).map((_, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <Skeleton key={index} variant="rounded" height="43px" width="100%" />
                  ))}
                </Stack>
              ) : (
                <Stack spacing={3}>
                  <Stack useFlexGap>
                    <Controller
                      name="specialNotes"
                      control={control}
                      render={({ field }) => (
                        <Autocomplete
                          {...field}
                          autoComplete
                          multiple
                          filterSelectedOptions
                          options={specialNotesOptions}
                          isOptionEqualToValue={(option: ComboBoxOption, value: ComboBoxOption) => option.value === value.value}
                          getOptionLabel={(option: ComboBoxOption) => option.label}
                          onChange={(_, value) => field.onChange(value)}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Special Notes"
                              fullWidth
                            />
                          )}
                        />
                      )}
                    />
                    <Controller
                      control={control}
                      name="additionalComments"
                      render={({ field }) => (
                        <RichTextEditor
                          editorName="Additional-Comments-Editor"
                          error={!!errors.additionalComments}
                          label="Additional Comments"
                          placeholder="Please provide any additional comments or phone call notes here."
                          defaultValue={defaultValues?.additionalComments}
                          onChange={field.onChange}
                          useLinks
                          useLists
                          sx={{
                            minHeight: '200px',
                            maxHeight: '400px',
                            flex: 1,
                            '& .MuiTypography-root': {
                              textWrap: 'auto',
                            },
                          }}
                        />
                      )}
                    />
                    <Typography variant="caption" color="textSecondary">
                      Please be mindful that any edits made here will be visible to all matched partners.
                    </Typography>
                  </Stack>

                  {submissionError && (
                  <Alert severity="error">
                    <AlertTitle>There was an error submitting the form.</AlertTitle>
                    {`Code: ${submissionError.code}: ${submissionError.message}`}
                  </Alert>
                  )}
                </Stack>
              )}
            </Box>
          </Box>
          <Box sx={(theme) => ({ borderTop: `1px solid ${theme.palette.divider}`, padding: 0 })}>
            <Stack spacing={2} direction="row" justifyContent="flex-start" sx={{ padding: 3 }}>
              <LoadingButton variant="contained" type="submit" {...getSubmitButtonProps(isSubmitting, isValid)}>
                Save
              </LoadingButton>
              <Button variant="outlined" onClick={onCancel || onClose}>
                Cancel
              </Button>
            </Stack>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default EditNotesModalView;
